/**
 * EndpointManagementPage.tsx
 *
 * LAPI Data Contract v1.1: Endpoint Management Dashboard
 *
 * Displays the active data contract, field-level access rules,
 * tier breakdown, and API endpoint documentation for lender admins.
 * Access is restricted via ProtectedRoute to admin roles.
 */

import { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiShield,
  FiLock,
  FiUnlock,
  FiEye,
  FiEyeOff,
  FiRefreshCw,
  FiSearch,
  FiXCircle,
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiAlertTriangle,
  FiDatabase,
  FiFileText,
  FiServer,
  FiCopy,
  FiPlay,
  FiCode,
  FiToggleLeft,
  FiToggleRight,
} from 'react-icons/fi';
import { SimpleTooltip } from '../components/ui/Tooltip';
import {
  getContract,
  ContractCatalogResponse,
  ContractField,
  LenderPurpose,
} from '../services/lenderApiService';

// ─── Info Tooltip helper ─────────────────────────────────────────────────────
const InfoTip: FC<{ tip: string }> = ({ tip }) => (
  <SimpleTooltip content={<span className="text-xs leading-relaxed">{tip}</span>} side="right" delayDuration={150}>
    <span className="inline-flex cursor-help">
      <FiFileText size={14} className="text-slate-400 hover:text-slate-600 transition-colors" />
    </span>
  </SimpleTooltip>
);

// ─── Tier Badge ──────────────────────────────────────────────────────────────
const TierBadge: FC<{ tier: number }> = ({ tier }) => {
  const styles: Record<number, string> = {
    1: 'bg-green-100 text-green-800 border-green-200',
    2: 'bg-amber-100 text-amber-800 border-amber-200',
    3: 'bg-red-100 text-red-800 border-red-200',
  };
  const labels: Record<number, string> = {
    1: 'Tier 1 — Approved',
    2: 'Tier 2 — Conditional',
    3: 'Tier 3 — Restricted',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[tier] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {labels[tier] || `Tier ${tier}`}
    </span>
  );
};

// ─── PII Level Badge ─────────────────────────────────────────────────────────
const PiiBadge: FC<{ level: string }> = ({ level }) => {
  const styles: Record<string, { cls: string; icon: React.ReactNode }> = {
    none: { cls: 'bg-green-50 text-green-700', icon: <FiUnlock size={10} /> },
    low: { cls: 'bg-blue-50 text-blue-700', icon: <FiEye size={10} /> },
    medium: { cls: 'bg-amber-50 text-amber-700', icon: <FiEyeOff size={10} /> },
    high: { cls: 'bg-red-50 text-red-700', icon: <FiLock size={10} /> },
  };
  const s = styles[level] || styles.none;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${s.cls}`}>
      {s.icon} {level}
    </span>
  );
};

// ─── Redaction Rule Badge ────────────────────────────────────────────────────
const RedactionBadge: FC<{ rule: string }> = ({ rule }) => {
  const styles: Record<string, string> = {
    none: 'bg-green-50 text-green-700',
    suppress: 'bg-red-50 text-red-700',
    mask_email: 'bg-amber-50 text-amber-700',
    initials_only: 'bg-blue-50 text-blue-700',
    hash: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-mono rounded-full ${styles[rule] || 'bg-slate-50 text-slate-600'}`}>
      {rule}
    </span>
  );
};

// ─── KPI Card ────────────────────────────────────────────────────────────────
interface KpiCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  subtitle?: string;
  status?: 'good' | 'warning' | 'neutral';
}

const KpiCard: FC<KpiCardProps> = ({ title, value, icon, subtitle, status = 'neutral' }) => {
  const bg: Record<string, string> = {
    good: 'border-green-200 bg-green-50',
    warning: 'border-amber-200 bg-amber-50',
    neutral: 'border-slate-200 bg-white',
  };
  return (
    <div className={`p-4 rounded-lg border-2 ${bg[status]} transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold mt-1 text-slate-800">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-lg bg-white/50 text-slate-600">{icon}</div>
      </div>
    </div>
  );
};

// ─── API Endpoint Card ───────────────────────────────────────────────────────
interface EndpointCardProps {
  method: 'GET' | 'POST';
  path: string;
  description: string;
  purpose: string;
  isNew?: boolean;
}

const EndpointCard: FC<EndpointCardProps> = ({ method, path, description, purpose, isNew }) => {
  const [copied, setCopied] = useState(false);
  const methodColor = method === 'GET' ? 'bg-blue-600' : 'bg-green-600';

  const handleCopy = () => {
    navigator.clipboard.writeText(path);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-2 mb-2">
        <span className={`px-2 py-0.5 text-xs font-bold text-white rounded ${methodColor}`}>{method}</span>
        <code className="text-sm font-mono text-slate-700 flex-1">{path}</code>
        {isNew && <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700 uppercase">New</span>}
        <button onClick={handleCopy} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Copy path">
          {copied ? <FiCheckCircle size={14} className="text-green-500" /> : <FiCopy size={14} />}
        </button>
      </div>
      <p className="text-sm text-slate-600">{description}</p>
      <p className="text-xs text-slate-400 mt-1">Purpose: <span className="font-medium">{purpose}</span></p>
    </div>
  );
};

// ─── Sort helpers ────────────────────────────────────────────────────────────
type FieldSortKey = 'field_name' | 'tier' | 'pii_level' | 'redaction_rule';
type SortDir = 'asc' | 'desc';

// ─── Preview types ──────────────────────────────────────────────────────────
type PreviewMode = 'sample' | 'live';
type PreviewEndpoint = 'members' | 'member-detail' | 'member-history' | 'contract';

const PREVIEW_ENDPOINTS: { value: PreviewEndpoint; label: string; path: string }[] = [
  { value: 'members', label: 'GET /lender/members', path: '/lender/members' },
  { value: 'member-detail', label: 'GET /lender/members/:memberId', path: '/lender/members/:memberId' },
  { value: 'member-history', label: 'GET /lender/members/:memberId/history', path: '/lender/members/:memberId/history' },
  { value: 'contract', label: 'GET /lender/contract', path: '/lender/contract' },
];

const PURPOSE_OPTIONS: { value: LenderPurpose; label: string }[] = [
  { value: 'lender_review', label: 'Lender Review (portfolio browsing)' },
  { value: 'underwriting', label: 'Underwriting (loan evaluation)' },
  { value: 'credit_check', label: 'Credit Check (decisioning)' },
  { value: 'fraud_prevention', label: 'Fraud Prevention (investigation)' },
];

/** Generate a synthetic sample value for a given field */
function syntheticValue(field: ContractField): unknown {
  const n = field.field_name;
  if (n === 'member_id') return 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';
  if (n === 'email') return 'j***@***.com';
  if (n === 'consent_status') return 'active';
  if (n === 'consent_granted_at') return '2026-03-15T10:30:00Z';
  if (n === 'verascore') return 82;
  if (n === 'verascore_rating') return 'Very Good';
  if (n === 'score_month') return '2026-04';
  if (n.startsWith('factor_')) return Math.floor(Math.random() * 40) + 60;
  if (n === 'income_band') return '75k_to_100k';
  if (n === 'income_basis_used') return 'DEPOSITED_NET';
  if (n === 'income_confidence') return 0.82;
  if (n.includes('score') && n !== 'verascore') return Math.floor(Math.random() * 20) + 65;
  if (n === 'dti_ratio_value') return 28.4;
  if (n === 'number_of_late_payments') return 0;
  if (n === 'age_of_late_payments') return null;
  if (n === 'approval_likelihood_band') return 'high';
  if (n === 'run_id') return 'run-00112233-4455-6677-8899-aabbccddeeff';
  if (n.includes('income') || n.includes('expense') || n.includes('debt') || n.includes('savings') || n.includes('reserves'))
    return Math.floor(Math.random() * 50000) + 10000;
  if (field.pii_level === 'high') return '[REDACTED]';
  if (field.pii_level === 'medium') return '***masked***';
  return `sample_${n}`;
}

/** Build a synthetic preview payload */
function buildSamplePayload(selectedFields: Set<string>, fields: ContractField[], endpoint: PreviewEndpoint): object {
  const memberObj: Record<string, unknown> = {};
  for (const f of fields) {
    if (selectedFields.has(f.field_name)) {
      memberObj[f.field_name] = syntheticValue(f);
    }
  }
  if (endpoint === 'contract') {
    return {
      status: 'success',
      data: {
        contract: { version_id: '1.1.0', status: 'active', effective_date: '2026-06-01', sunset_date: null, changelog: 'Sample preview' },
        fields: fields.filter(f => selectedFields.has(f.field_name)).map(f => ({
          field_name: f.field_name, tier: f.tier, pii_level: f.pii_level,
          redaction_rule: f.redaction_rule, consent_scope: f.consent_scope, description: f.description,
        })),
        meta: { total_fields: selectedFields.size, timestamp: new Date().toISOString() },
      },
    };
  }
  const members = endpoint === 'member-detail' ? undefined : [memberObj];
  const member = endpoint === 'member-detail' ? memberObj : undefined;
  return {
    status: 'success',
    data: {
      ...(members ? { members } : { member }),
      meta: {
        contract_version: '1.1.0',
        fields_returned: [...selectedFields],
        fields_suppressed: fields.filter(f => !selectedFields.has(f.field_name)).map(f => f.field_name),
        timestamp: new Date().toISOString(),
        ...(members ? { count: 1 } : {}),
      },
    },
  };
}

// ─── Main Component ─────────────────────────────────────────────────────────
const EndpointManagementPage: FC = () => {
  const [contract, setContract] = useState<ContractCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<FieldSortKey>('tier');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expandedField, setExpandedField] = useState<string | null>(null);

  // ─── Data Preview state ─────────────────────────────────────────────────
  const [previewMode, setPreviewMode] = useState<PreviewMode>('sample');
  const [previewEndpoint, setPreviewEndpoint] = useState<PreviewEndpoint>('members');
  const [previewPurpose, setPreviewPurpose] = useState<LenderPurpose>('lender_review');
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [previewJson, setPreviewJson] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewCopied, setPreviewCopied] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Member selection state ─────────────────────────────────────────────
  interface CachedMember { member_id: string; email: string; auth0_user_id?: string; verascore: number; verascore_rating: string; member_name?: string; first_name?: string; last_name?: string; }
  const [cachedMembers, setCachedMembers] = useState<CachedMember[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [membersLoaded, setMembersLoaded] = useState(false);

  // ─── VeraScore filter state ─────────────────────────────────────────────
  const [scoreFilterEnabled, setScoreFilterEnabled] = useState(false);
  const [scoreMin, setScoreMin] = useState<string>('');
  const [scoreMax, setScoreMax] = useState<string>('');
  const [scoreExact, setScoreExact] = useState<string>('');
  const [scoreFilterMode, setScoreFilterMode] = useState<'range' | 'exact'>('range');

  const fetchContract = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getContract();
      if (!data) throw new Error('No contract data returned');
      setContract(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  // Initialize selected fields from Tier 1 when contract loads
  useEffect(() => {
    if (contract?.fields && selectedFields.size === 0) {
      const tier1 = new Set(contract.fields.filter(f => f.tier === 1).map(f => f.field_name));
      setSelectedFields(tier1);
    }
  }, [contract]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Preview logic ──────────────────────────────────────────────────────
  const API_BASE = import.meta.env.VITE_API_URL || '/api';
  const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

  // Fetch and cache members list for member selector
  const fetchMembersList = useCallback(async () => {
    if (membersLoaded) return cachedMembers;
    try {
      const res = await fetch(`${API_BASE}/verascore/lender/members`, {
        credentials: 'include',
        headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json', 'x-request-purpose': 'lender_review' },
      });
      if (!res.ok) return [];
      const data = await res.json();
      const members: CachedMember[] = (data?.data?.members || data?.members || []).map((m: any) => ({
        member_id: m.member_id,
        email: m.email || '',
        auth0_user_id: m.auth0_user_id || '',
        verascore: m.verascore ?? 0,
        verascore_rating: m.verascore_rating || '',
        member_name: m.member_name || `${m.first_name || ''} ${m.last_name || ''}`.trim(),
        first_name: m.first_name,
        last_name: m.last_name,
      }));
      setCachedMembers(members);
      setMembersLoaded(true);
      if (members.length > 0 && !selectedMemberId) setSelectedMemberId(members[0].member_id);
      return members;
    } catch { return []; }
  }, [API_BASE, VS_CLIENT_ID, membersLoaded, cachedMembers, selectedMemberId]);

  // Load members when switching to live mode or member-specific endpoints
  useEffect(() => {
    if (previewMode === 'live' && !membersLoaded) { fetchMembersList(); }
  }, [previewMode, membersLoaded, fetchMembersList]);

  // Filtered member list for the search dropdown
  // Check if input looks like a UUID
  const isUuidLike = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
  // Check if input looks like an Auth0 ID
  const isAuth0Like = (s: string) => /^(auth0|google-oauth2|github)\|/i.test(s.trim());

  const memberSearchResults = useMemo(() => {
    if (!memberSearch.trim()) return cachedMembers;
    const q = memberSearch.toLowerCase().trim();
    return cachedMembers.filter(m =>
      m.email.toLowerCase().includes(q) ||
      m.member_id.toLowerCase().includes(q) ||
      (m.auth0_user_id || '').toLowerCase().includes(q) ||
      (m.member_name || '').toLowerCase().includes(q)
    );
  }, [cachedMembers, memberSearch]);

  // Allow direct member ID usage when search doesn't match cached list
  const handleUseDirectId = () => {
    const q = memberSearch.trim();
    if (q) {
      setSelectedMemberId(q);
      setMemberSearch('');
    }
  };

  // VeraScore filter helper
  const applyScoreFilter = (members: any[]): any[] => {
    if (!scoreFilterEnabled) return members;
    if (scoreFilterMode === 'exact' && scoreExact) {
      const val = parseInt(scoreExact, 10);
      if (!isNaN(val)) return members.filter((m: any) => m.verascore === val);
    }
    if (scoreFilterMode === 'range') {
      const min = scoreMin ? parseInt(scoreMin, 10) : 0;
      const max = scoreMax ? parseInt(scoreMax, 10) : 100;
      return members.filter((m: any) => {
        const s = m.verascore ?? 0;
        return s >= min && s <= max;
      });
    }
    return members;
  };

  const generatePreview = useCallback(async () => {
    if (!contract?.fields) return;

    if (previewMode === 'sample') {
      const payload = buildSamplePayload(selectedFields, contract.fields, previewEndpoint);
      setPreviewJson(JSON.stringify(payload, null, 2));
      setPreviewError(null);
      return;
    }

    // Live mode — call real API
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      // Ensure members are loaded
      const members = cachedMembers.length > 0 ? cachedMembers : await fetchMembersList();
      const targetMemberId = selectedMemberId || members[0]?.member_id;

      let url = `${API_BASE}/verascore/lender/members`;
      if (previewEndpoint === 'contract') url = `${API_BASE}/verascore/lender/contract`;
      if (previewEndpoint === 'member-detail') {
        if (!targetMemberId) throw new Error('No member selected for preview');
        url = `${API_BASE}/verascore/lender/members/${targetMemberId}`;
      }
      if (previewEndpoint === 'member-history') {
        if (!targetMemberId) throw new Error('No member selected for preview');
        url = `${API_BASE}/verascore/lender/members/${targetMemberId}/history?months=6&includeFactors=true&includeInputs=true`;
      }

      const res = await fetch(url, {
        credentials: 'include',
        headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json', 'x-request-purpose': previewPurpose },
      });
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const raw = await res.json();

      // Client-side filter to selected fields only (skip for contract endpoint)
      if (previewEndpoint !== 'contract' && raw?.data) {
        const filterObj = (obj: Record<string, unknown>) => {
          const filtered: Record<string, unknown> = {};
          for (const key of Object.keys(obj)) {
            if (selectedFields.has(key) || key === 'member_id') filtered[key] = obj[key];
          }
          return filtered;
        };
        if (raw.data.members) {
          // Apply VeraScore filtering
          raw.data.members = applyScoreFilter(raw.data.members);
          raw.data.members = raw.data.members.map((m: Record<string, unknown>) => filterObj(m));
          if (raw.data.meta) raw.data.meta.count = raw.data.members.length;
        }
        if (raw.data.member) {
          raw.data.member = filterObj(raw.data.member as Record<string, unknown>);
        }
      }
      setPreviewJson(JSON.stringify(raw, null, 2));
    } catch (err: any) {
      setPreviewError(err.message || 'Preview failed');
      setPreviewJson(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [contract, previewMode, previewEndpoint, previewPurpose, selectedFields, selectedMemberId, scoreFilterEnabled, scoreFilterMode, scoreMin, scoreMax, scoreExact, API_BASE, VS_CLIENT_ID, cachedMembers, fetchMembersList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced preview update
  useEffect(() => {
    if (!contract?.fields) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { generatePreview(); }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [generatePreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleField = (fieldName: string) => {
    setSelectedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) next.delete(fieldName); else next.add(fieldName);
      return next;
    });
  };

  const selectAllFields = () => {
    if (contract?.fields) setSelectedFields(new Set(contract.fields.map(f => f.field_name)));
  };
  const selectNoneFields = () => setSelectedFields(new Set());
  const selectTierFields = (tier: number) => {
    if (contract?.fields) setSelectedFields(new Set(contract.fields.filter(f => f.tier <= tier).map(f => f.field_name)));
  };

  const copyPreview = () => {
    if (previewJson) { navigator.clipboard.writeText(previewJson); setPreviewCopied(true); setTimeout(() => setPreviewCopied(false), 2000); }
  };

  // Filtered + sorted fields
  const q = search.toLowerCase().trim();
  const filteredFields = useMemo(() => {
    if (!contract?.fields) return [];
    let fields = contract.fields.filter((f) => {
      if (tierFilter !== null && f.tier !== tierFilter) return false;
      if (q && !f.field_name.toLowerCase().includes(q) && !(f.description || '').toLowerCase().includes(q)) return false;
      return true;
    });
    return [...fields].sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [contract, q, tierFilter, sortKey, sortDir]);

  const toggleSort = (key: FieldSortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon: FC<{ col: FieldSortKey }> = ({ col }) => {
    if (sortKey !== col) return <FiChevronDown size={12} className="inline ml-0.5 opacity-30" />;
    return sortDir === 'asc'
      ? <FiChevronUp size={12} className="inline ml-0.5 text-blue-600" />
      : <FiChevronDown size={12} className="inline ml-0.5 text-blue-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Endpoint Management...</p>
        </div>
      </div>
    );
  }

  const t1 = contract?.tiers?.tier_1_approved?.length ?? 0;
  const t2 = contract?.tiers?.tier_2_conditional?.length ?? 0;
  const t3 = contract?.tiers?.tier_3_restricted?.length ?? 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiServer className="text-brand-blue" /> Endpoint Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            LAPI Data Contract v{contract?.contract?.version_id || '—'} • API Field Registry & Access Rules
          </p>
        </div>
        <button
          onClick={fetchContract}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <FiXCircle className="inline mr-1" /> {error}
        </div>
      )}

      {/* Contract Info Banner */}
      {contract?.contract && (
        <div className="mb-6 p-4 bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-lg">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Contract Version</p>
              <p className="text-lg font-bold text-slate-800">{contract.contract.version_id}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Status</p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                <FiCheckCircle size={12} /> {contract.contract.status}
              </span>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Effective Date</p>
              <p className="text-sm font-medium text-slate-700">
                {contract.contract.effective_date ? new Date(contract.contract.effective_date).toLocaleDateString() : '—'}
              </p>
            </div>
            {contract.contract.sunset_date && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Sunset Date</p>
                <p className="text-sm font-medium text-amber-700">
                  <FiAlertTriangle className="inline mr-1" size={12} />
                  {new Date(contract.contract.sunset_date).toLocaleDateString()}
                </p>
              </div>
            )}
            {contract.contract.changelog && (
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Changelog</p>
                <p className="text-sm text-slate-600">{contract.contract.changelog}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard title="Total Fields" value={contract?.meta?.total_fields ?? 0} icon={<FiDatabase size={20} />} />
        <KpiCard title="Tier 1 — Approved" value={t1} icon={<FiUnlock size={20} />} status="good" subtitle="Always visible" />
        <KpiCard title="Tier 2 — Conditional" value={t2} icon={<FiEye size={20} />} status="warning" subtitle="Purpose-gated" />
        <KpiCard title="Tier 3 — Restricted" value={t3} icon={<FiLock size={20} />} subtitle="Underwriting only" />
      </div>

      {/* API Endpoints */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FiServer className="text-blue-500" /> API Endpoints <InfoTip tip="Available Lenders API endpoints. All endpoints require cookie-based session authentication and include the X-VS-Purpose header for audit logging." />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EndpointCard method="GET" path="/verascore/lender/members" description="List all members shared with your organization. Returns contract-filtered fields with redaction applied." purpose="portfolio_review, lender_review" />
          <EndpointCard method="GET" path="/verascore/lender/members/:memberId" description="Get detailed member profile with factor scores, income data, and credit completeness." purpose="lender_review, underwriting" />
          <EndpointCard method="GET" path="/verascore/lender/members/:memberId/history" description="Historical VeraScore trend data with optional factor and input breakdowns." purpose="lender_review, underwriting" />
          <EndpointCard method="POST" path="/verascore/lender/members/query" description="Batch lookup by member IDs (max 50). Deduplicates and validates UUIDs." purpose="lender_review" isNew />
          <EndpointCard method="GET" path="/verascore/lender/contract" description="Contract catalog — field registry, tier breakdown, and version metadata." purpose="any (metadata only)" isNew />
          <EndpointCard method="POST" path="/verascore/lender/access-events" description="Log a lender access event for audit trail (view, download, export)." purpose="lender_review" />
        </div>
      </div>

      {/* ─── Data Preview Section ──────────────────────────────────────────── */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FiCode className="text-indigo-500" /> Data Preview
          <InfoTip tip="Preview the exact API response payload. Select fields, endpoint, and purpose to see what data your integration will receive. Sample mode uses synthetic data; Live mode calls the real API with masking applied." />
        </h2>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Controls Bar */}
          <div className="p-4 bg-gradient-to-r from-indigo-50 to-slate-50 border-b border-slate-200">
            <div className="flex flex-wrap items-center gap-3">
              {/* Endpoint selector */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Endpoint</label>
                <select
                  value={previewEndpoint}
                  onChange={(e) => setPreviewEndpoint(e.target.value as PreviewEndpoint)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  {PREVIEW_ENDPOINTS.map(ep => (
                    <option key={ep.value} value={ep.value}>{ep.label}</option>
                  ))}
                </select>
              </div>

              {/* Purpose selector */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Purpose</label>
                <select
                  value={previewPurpose}
                  onChange={(e) => setPreviewPurpose(e.target.value as LenderPurpose)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  {PURPOSE_OPTIONS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              {/* Mode toggle */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Mode</label>
                <button
                  onClick={() => setPreviewMode(m => m === 'sample' ? 'live' : 'sample')}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    previewMode === 'live'
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-slate-300 text-slate-600'
                  }`}
                >
                  {previewMode === 'live' ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                  {previewMode === 'sample' ? 'Sample Data' : 'Live Masked'}
                </button>
              </div>

              {/* Refresh */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-transparent font-medium">Action</label>
                <button
                  onClick={generatePreview}
                  disabled={previewLoading}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {previewLoading ? <FiRefreshCw size={14} className="animate-spin" /> : <FiPlay size={14} />}
                  {previewLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            {/* ─── Member Selector (for member-detail / member-history) ──────── */}
            {(previewEndpoint === 'member-detail' || previewEndpoint === 'member-history') && (
              <div className="mt-3 pt-3 border-t border-slate-200/60">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex flex-col gap-1 flex-1 min-w-[250px]">
                    <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
                      Search Member (email, name, UUID, or Auth0 ID)
                    </label>
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                      <input
                        type="text"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        placeholder="Search by name, UUID, or paste a member ID…"
                        className="w-full pl-9 pr-4 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      />
                      {memberSearch && (
                        <button onClick={() => setMemberSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          <FiXCircle size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {/* Member results list */}
                <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg bg-white">
                  {memberSearchResults.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-slate-400">
                      {!membersLoaded ? (
                        'Switch to Live mode to load members.'
                      ) : memberSearch.trim() ? (
                        <div className="space-y-2">
                          <p>No cached members match your search.</p>
                          <p className="text-[10px] text-slate-400">💡 Emails may be masked by PII redaction. Try searching by name or UUID instead.</p>
                          {(isUuidLike(memberSearch) || isAuth0Like(memberSearch)) && (
                            <button
                              onClick={handleUseDirectId}
                              className="mt-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                            >
                              Use &quot;{memberSearch.trim().slice(0, 30)}{memberSearch.trim().length > 30 ? '…' : ''}&quot; directly
                            </button>
                          )}
                          {!isUuidLike(memberSearch) && !isAuth0Like(memberSearch) && (
                            <button
                              onClick={handleUseDirectId}
                              className="mt-1 px-3 py-1 text-[10px] font-medium rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                            >
                              Use as direct lookup value →
                            </button>
                          )}
                        </div>
                      ) : (
                        'No members found.'
                      )}
                    </div>
                  ) : (
                    memberSearchResults.map(m => (
                      <button
                        key={m.member_id}
                        onClick={() => { setSelectedMemberId(m.member_id); setMemberSearch(''); }}
                        className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-0 hover:bg-indigo-50 transition-colors ${
                          selectedMemberId === m.member_id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-slate-700">{m.member_name || m.email}</span>
                            {m.member_name && <span className="text-slate-400 ml-2">{m.email}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                              m.verascore >= 80 ? 'bg-green-100 text-green-700' :
                              m.verascore >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {m.verascore} {m.verascore_rating}
                            </span>
                            {selectedMemberId === m.member_id && <FiCheckCircle size={12} className="text-indigo-500" />}
                          </div>
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono mt-0.5 truncate">{m.member_id}</div>
                      </button>
                    ))
                  )}
                </div>
                {selectedMemberId && (
                  <div className="mt-2 text-xs text-slate-500">
                    Selected: <span className="font-mono text-indigo-600">{selectedMemberId}</span>
                  </div>
                )}
              </div>
            )}

            {/* ─── VeraScore Filter (for members list endpoint) ──────────────── */}
            {previewEndpoint === 'members' && previewMode === 'live' && (
              <div className="mt-3 pt-3 border-t border-slate-200/60">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scoreFilterEnabled}
                      onChange={(e) => setScoreFilterEnabled(e.target.checked)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                    />
                    <span className="font-medium">Filter by VeraScore</span>
                  </label>

                  {scoreFilterEnabled && (
                    <>
                      {/* Mode toggle: range vs exact */}
                      <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setScoreFilterMode('range')}
                          className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${
                            scoreFilterMode === 'range' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                          }`}
                        >Range</button>
                        <button
                          onClick={() => setScoreFilterMode('exact')}
                          className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${
                            scoreFilterMode === 'exact' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                          }`}
                        >Exact</button>
                      </div>

                      {scoreFilterMode === 'range' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number" min="0" max="100" placeholder="Min"
                            value={scoreMin} onChange={(e) => setScoreMin(e.target.value)}
                            className="w-16 px-2 py-1 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          />
                          <span className="text-xs text-slate-400">to</span>
                          <input
                            type="number" min="0" max="100" placeholder="Max"
                            value={scoreMax} onChange={(e) => setScoreMax(e.target.value)}
                            className="w-16 px-2 py-1 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          />
                        </div>
                      ) : (
                        <input
                          type="number" min="0" max="100" placeholder="Score"
                          value={scoreExact} onChange={(e) => setScoreExact(e.target.value)}
                          className="w-20 px-2 py-1 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />
                      )}

                      {scoreFilterEnabled && (
                        <span className="text-[10px] text-slate-400">
                          {scoreFilterMode === 'range'
                            ? `Showing members with VeraScore ${scoreMin || '0'} – ${scoreMax || '100'}`
                            : `Showing members with VeraScore = ${scoreExact || '?'}`}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Field quick-select buttons */}
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-200/60">
              <span className="text-xs text-slate-500 font-medium">Fields:</span>
              <button onClick={selectAllFields} className="px-2 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">All</button>
              <button onClick={selectNoneFields} className="px-2 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">None</button>
              <button onClick={() => selectTierFields(1)} className="px-2 py-0.5 text-[10px] font-medium rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors">Tier 1</button>
              <button onClick={() => selectTierFields(2)} className="px-2 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors">Tier 1+2</button>
              <button onClick={() => selectTierFields(3)} className="px-2 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors">Tier 1+2+3</button>
              <span className="text-xs text-slate-400 ml-2">({selectedFields.size} selected)</span>
            </div>

            {/* Field checkboxes */}
            <div className="mt-3 max-h-32 overflow-y-auto border border-slate-200 rounded-lg bg-white p-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
                {(contract?.fields || []).map(f => (
                  <label key={f.field_name} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                    <input
                      type="checkbox"
                      checked={selectedFields.has(f.field_name)}
                      onChange={() => toggleField(f.field_name)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                    />
                    <span className="font-mono truncate" title={f.description}>{f.field_name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* JSON Viewer */}
          <div className="relative">
            <div className="absolute top-2 right-2 z-10">
              <button onClick={copyPreview} className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-white/90 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-white transition-colors shadow-sm">
                {previewCopied ? <><FiCheckCircle size={12} className="text-green-500" /> Copied</> : <><FiCopy size={12} /> Copy</>}
              </button>
            </div>
            {previewError && (
              <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <FiXCircle className="inline mr-1" /> {previewError}
              </div>
            )}
            {previewLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!previewLoading && previewJson && (
              <pre className="p-4 text-xs font-mono text-slate-700 bg-slate-50 overflow-x-auto max-h-96 overflow-y-auto leading-relaxed whitespace-pre">
                {previewJson}
              </pre>
            )}
            {!previewLoading && !previewJson && !previewError && (
              <div className="py-12 text-center text-slate-400 text-sm">
                Select fields and click <strong>Refresh</strong> to generate a preview.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Field Registry Table */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FiShield className="text-emerald-500" /> Field Registry <InfoTip tip="Complete list of fields available through the Lenders API. Each field has a tier classification, PII level, and default redaction rule. Fields not listed here are never exposed." />
          <span className="text-xs font-normal text-slate-400">({filteredFields.length} of {contract?.meta?.total_fields ?? 0})</span>
        </h2>

        {/* Search + Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fields by name or description…"
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <FiXCircle size={16} />
              </button>
            )}
          </div>
          {[null, 1, 2, 3].map((t) => (
            <button
              key={t ?? 'all'}
              onClick={() => setTierFilter(t)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                tierFilter === t ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {t === null ? 'All Tiers' : `Tier ${t}`}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('field_name')}>
                  Field Name <SortIcon col="field_name" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('tier')}>
                  Tier <SortIcon col="tier" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('pii_level')}>
                  PII Level <SortIcon col="pii_level" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('redaction_rule')}>
                  Redaction <SortIcon col="redaction_rule" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Consent Scope</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Description</th>
              </tr>
            </thead>
            <tbody>
              {filteredFields.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    No fields match your search.
                  </td>
                </tr>
              ) : (
                filteredFields.map((f) => (
                  <tr
                    key={f.field_name}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedField(expandedField === f.field_name ? null : f.field_name)}
                  >
                    <td className="px-4 py-3 font-mono text-xs font-medium text-slate-700">{f.field_name}</td>
                    <td className="px-4 py-3"><TierBadge tier={f.tier} /></td>
                    <td className="px-4 py-3"><PiiBadge level={f.pii_level} /></td>
                    <td className="px-4 py-3"><RedactionBadge rule={f.redaction_rule} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{f.consent_scope || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate" title={f.description}>{f.description || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tier Breakdown (visual summary) */}
      {contract?.tiers && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <FiDatabase className="text-purple-500" /> Tier Breakdown <InfoTip tip="Fields grouped by data classification tier. Tier 1 fields are always visible. Tier 2 requires underwriting or credit_check purpose. Tier 3 requires explicit underwriting purpose with active consent." />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tier 1 */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FiUnlock size={16} className="text-green-600" />
                <h3 className="text-sm font-semibold text-green-800">Tier 1 — Approved ({t1})</h3>
              </div>
              <p className="text-xs text-green-700 mb-2">Always visible to authenticated lenders.</p>
              <div className="flex flex-wrap gap-1">
                {contract.tiers.tier_1_approved.map((f) => (
                  <span key={f} className="px-2 py-0.5 text-[10px] font-mono bg-green-100 text-green-700 rounded">{f}</span>
                ))}
              </div>
            </div>
            {/* Tier 2 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FiEye size={16} className="text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-800">Tier 2 — Conditional ({t2})</h3>
              </div>
              <p className="text-xs text-amber-700 mb-2">Visible with underwriting or credit_check purpose.</p>
              <div className="flex flex-wrap gap-1">
                {contract.tiers.tier_2_conditional.map((f) => (
                  <span key={f} className="px-2 py-0.5 text-[10px] font-mono bg-amber-100 text-amber-700 rounded">{f}</span>
                ))}
              </div>
            </div>
            {/* Tier 3 */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FiLock size={16} className="text-red-600" />
                <h3 className="text-sm font-semibold text-red-800">Tier 3 — Restricted ({t3})</h3>
              </div>
              <p className="text-xs text-red-700 mb-2">Underwriting purpose only. Requires active consent.</p>
              <div className="flex flex-wrap gap-1">
                {contract.tiers.tier_3_restricted.map((f) => (
                  <span key={f} className="px-2 py-0.5 text-[10px] font-mono bg-red-100 text-red-700 rounded">{f}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-xs text-slate-400 py-4 border-t border-slate-200">
        Contract data fetched at {contract?.meta?.timestamp ? new Date(contract.meta.timestamp).toLocaleString() : '—'}
        {' • '}Deny-by-default: fields not listed above are never exposed.
      </div>
    </div>
  );
};

export default EndpointManagementPage;
